import LoggingAPI from "@/api/logging";
import MonolithNotesAPI from "@/api/Notes";
import { Note } from "@/Features/Notes";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatures } from "@/contexts/FeaturesContext";
import {
  Button,
  Controls,
  Extensions,
  Loader,
  RichTextEditor,
  SlashCommands,
  styled,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { HandleImageUpload } from "@monolith-forensics/monolith-ui/dist/RichTextEditor/Plugins/UploadImagesPlugin";
import { createImageUrlUploadHandler } from "@/utils/noteImageUrlUpload";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon } from "lucide-react";
import moment from "moment";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;

  margin-top: 10px;
`;

const NoteMenu = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const NoteEditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0px;

  border: 1px solid ${({ theme }) => theme.palette.input.border};
  border-radius: 4px;
  padding-top: 10px;
  padding-bottom: 10px;

  overflow-y: auto;
`;

export const NoteEditor: React.FC<{
  defaultNoteData: Note;
  onClose?: () => void;
}> = ({ defaultNoteData, onClose }) => {
  const { currentUser, hasPermission } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  const hasTableSupport = hasFeatures([
    FEATURES.NOTES_AND_REPORTS_TABLE_SUPPORT,
  ]);
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<boolean>(false);

  const { data, isLoading } = useQuery({
    queryKey: [
      "case-notes",
      "list",
      { uuid: defaultNoteData.uuid, include_content: true },
    ],
    queryFn: ({ queryKey }) => {
      const [, , query] = queryKey;
      return MonolithNotesAPI.getNotes(query);
    },
  });

  const updateNoteData = useMutation({
    mutationFn: MonolithNotesAPI.updateNote,
    onMutate: async () => {
      setSaving(true);
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [
          "case-notes",
          "list",
          { uuid: defaultNoteData.uuid, include_content: true },
        ],
        (oldData: { data: Note[] }) => {
          const cachedNote = oldData?.data?.[0];
          if (!cachedNote) {
            return oldData;
          }
          const newData = {} as any;
          if (variables.note_data !== undefined) {
            newData.note_data = variables.note_data;
          }
          if (variables.note_tag !== undefined) {
            newData.note_tag = variables.note_tag;
          }
          return {
            ...oldData,
            data: [
              {
                ...cachedNote,
                ...newData,
              },
            ],
          };
        }
      );

      // Add delay to ensure that user can see the saving state
      // sometimes the update happens so fast that the saving state is not visible
      setTimeout(() => {
        setSaving(false);
      }, 300);

      LoggingAPI.createLog({
        case_id: defaultNoteData.case_id,
        user_id: currentUser.user_id,
        message: `Updated timeline event note.`,
        event: "update_timeline_event",
        object_type: "timeline_event",
        object_uuid: defaultNoteData.object_id,
      });

      LoggingAPI.createLog({
        case_id: defaultNoteData.case_id,
        user_id: currentUser.user_id,
        message: `Updated case note.`,
        event: "update_case_note",
        object_type: "case_note",
        object_uuid: defaultNoteData.uuid,
      });
    },
  });

  const handleUpdateNoteTitle = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let newTitle = e.target.value;
    if (newTitle === "") {
      newTitle = defaultNoteData.note_tag;
    }
    updateNoteData.mutate({
      uuid: defaultNoteData.uuid,
      note_tag: newTitle,
    });
  };

  const handleChange = async (e: string) => {
    updateNoteData.mutate({
      uuid: defaultNoteData.uuid,
      note_data: e,
    });
  };

  const handleImageUpload: HandleImageUpload = async (data) => {
    return new Promise(async (resolve) => {
      const uploadURL = await MonolithNotesAPI.getUploadUrl({
        filename: data.name,
        case_uuid: defaultNoteData?.linked_case.uuid,
      });

      await MonolithNotesAPI.uploadImage({
        url: uploadURL.uploadUrl,
        file: data.file,
      });

      let image = new Image();
      image.src = uploadURL.downloadUrl;
      image.onload = () => {
        resolve(uploadURL.downloadUrl);
      };

      // Send API request to add attachment record for note and img
      MonolithNotesAPI.addAttachment({
        note_uuid: defaultNoteData?.uuid,
        uuid: data.id,
        filename: data.name,
        size: data.file.size,
        created_on: moment().toISOString(),
        md5: data.md5,
        sha1: data.sha1,
        sha256: data.sha256,
      });
    });
  };

  const handleImageUrlUpload = createImageUrlUploadHandler(() => ({
    case_uuid: defaultNoteData?.linked_case?.uuid,
    note_uuid: defaultNoteData?.uuid,
  }));

  const debouncedHandleChange = useDebouncedCallback((e) => {
    handleChange(e);
  }, 400);

  const debouncedUpdateNoteTitle = useDebouncedCallback((e) => {
    handleUpdateNoteTitle(e);
  }, 350);

  if (isLoading) return <Loader />;

  const noteData = data?.data?.[0] || defaultNoteData || {};

  const ownNote = noteData.created_by.user_id === currentUser.user_id;
  const readOnly = !hasPermission() || !ownNote;

  return (
    <StyledContainer>
      <NoteMenu>
        <div style={{ width: "100%" }}>
          <TextInput
            size="md"
            defaultValue={noteData?.note_tag}
            placeholder="Enter Note Title"
            style={{ backgroundColor: "transparent" }}
            onChange={debouncedUpdateNoteTitle}
          />
        </div>
        <Button
          size="xxs"
          variant="outlined"
          leftSection={<ArrowLeftIcon size={16} />}
          onClick={() => onClose?.()}
          title="Back to Notes List"
        >
          Back
        </Button>
      </NoteMenu>
      <NoteEditorContainer>
        {hasTableSupport ? (
          <RichTextEditor
            style={{ height: "100%" }}
            autoFocus={true}
            saving={saving}
            showToolbar={!readOnly}
            defaultValue={noteData?.note_data}
            handleImageUpload={handleImageUpload}
            handleImageUrlUpload={handleImageUrlUpload}
            onChange={debouncedHandleChange}
            readOnly={readOnly}
            extensions={[
              Extensions.Placeholder,
              Extensions.SlashCommand,
              Extensions.BubbleMenu,
              Extensions.Image,
            ]}
            slashCommands={Object.values(SlashCommands)}
            toolbarOptions={{
              controls: Object.values(Controls).filter(
                (c) => c !== Controls.FONT
              ),
            }}
          />
        ) : (
          <RichTextEditor
            style={{ height: "100%" }}
            autoFocus={true}
            saving={saving}
            showToolbar={!readOnly}
            defaultValue={noteData?.note_data}
            handleImageUpload={handleImageUpload}
            handleImageUrlUpload={handleImageUrlUpload}
            onChange={debouncedHandleChange}
            readOnly={readOnly}
            extensions={[
              Extensions.Placeholder,
              Extensions.SlashCommand,
              Extensions.BubbleMenu,
              Extensions.Image,
            ]}
            slashCommands={Object.values(SlashCommands)}
            toolbarOptions={{
              controls: Object.values(Controls).filter(
                (c) => c !== Controls.FONT
              ),
            }}
          />
        )}
      </NoteEditorContainer>
    </StyledContainer>
  );
};
